import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../../../ui/card';
import { ArrowRight, TrendingUp } from 'lucide-react';

const FunnelStep = ({ data, color, isLast, maxCount }) => {
  const percentage = maxCount > 0 ? (data.count / maxCount) * 100 : 0;

  return (
    <div className="flex-1 flex flex-col items-center">
      <div className="text-center mb-3">
        <motion.p
          className="text-2xl font-bold text-gray-900 font-outfit"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          {data.count.toLocaleString()}
        </motion.p>
        <p className="text-xs font-medium text-gray-500 mt-1">{data.label}</p>
      </div>

      <div className="w-full flex items-center gap-2">
        <div className="flex-1 h-8 bg-stone-100 rounded-lg overflow-hidden relative">
          <motion.div
            className={`h-full ${color} rounded-lg`}
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-semibold text-gray-700">
              {data.rate}%
            </span>
          </div>
        </div>
        {!isLast && (
          <ArrowRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};

export function BusinessFunnel({ funnel, loading }) {
  if (loading || !funnel) {
    return (
      <Card className="p-5 bg-white border-stone-200/80">
        <div className="h-[150px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Card>
    );
  }

  const steps = [
    { data: funnel.students, color: 'bg-blue-500' },
    { data: funnel.applications, color: 'bg-indigo-500' },
    { data: funnel.approved, color: 'bg-[#F2575F]' },
    { data: funnel.paid, color: 'bg-emerald-500' }
  ];

  const maxCount = Math.max(...steps.map(s => s.data.count)) || 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <Card className="p-5 bg-white border-stone-200/80">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-stone-50">
              <TrendingUp className="w-4 h-4 text-gray-600" />
            </div>
            <h3 className="font-semibold text-gray-900 font-outfit">Conversion Funnel</h3>
          </div>
          <span className="text-xs text-gray-500 bg-stone-50 px-2.5 py-1 rounded-lg border border-stone-100">
            {funnel.period}
          </span>
        </div>

        <div className="flex items-end gap-2">
          {steps.map((step, index) => (
            <FunnelStep
              key={index}
              data={step.data}
              color={step.color}
              isLast={index === steps.length - 1}
              maxCount={maxCount}
            />
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-stone-100">
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">
              Student → Application: <span className="font-semibold text-gray-700">{funnel.applications?.rate}%</span>
            </span>
            <span className="text-gray-500">
              Approved → Paid: <span className="font-semibold text-gray-700">{funnel.paid?.rate}%</span>
            </span>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default BusinessFunnel;
